import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState, ThemeMode } from 'sama-shared';

/*
 * Hook canónico del sistema "Brasa Nocturna" (3 temas: light | dark | ocean).
 * Devuelve superficies y texto cálidos según el tema activo.
 * REGLA: ningún Drawer, Modal, Card, inset o microcomponente debe hardcodear
 * grises (#fafafa, #f5f5f5, #fff, #141414, #1f1f1f, #333, #303030...).
 * Se usan estos valores. Los componentes AntD sin override ya reciben los
 * tokens cálidos vía PartnerThemeProvider.
 *
 * REGLA 2: nunca compares `theme === 'dark'` para ramificar estilos —
 * 'ocean' también es oscuro. Usa `isDarkTheme(theme)` (= theme !== 'light').
 */

export type { ThemeMode };

/** 'dark' y 'ocean' comparten rama oscura; solo 'light' es claro. */
export const isDarkTheme = (theme: string | undefined | null): boolean => theme !== 'light';

export interface BrasaTheme {
  isDark: boolean;
  mode: ThemeMode;
  /** Acento de marca — CONSTANTE en los 3 temas (identidad, no tema) */
  accent: string;
  accentContrast: string;
  /** Superficie base de tarjetas / cuerpo de drawer */
  bgBase: string;
  /** Superficie elevada (hover, popovers internos) */
  bgRaised: string;
  /** Superficie hundida: insets, cajas de totales, listas internas */
  bgInset: string;
  /** Variante inset alternativa (bandas, placeholders) */
  bgInsetAlt: string;
  /** Borde estándar whisper */
  line: string;
  /** Borde suave para separaciones internas */
  lineSoft: string;
  tx1: string;
  tx2: string;
  tx3: string;
}

const DARK: Omit<BrasaTheme, 'isDark' | 'mode'> = {
  accent: '#ff7a1a',
  accentContrast: '#ffffff',
  bgBase: '#1e1814',
  bgRaised: '#262019',
  bgInset: '#14100c',
  bgInsetAlt: '#1a1410',
  line: 'rgba(255,214,170,0.09)',
  lineSoft: 'rgba(255,214,170,0.055)',
  tx1: '#fff7ed',
  tx2: '#e7d9c8',
  tx3: '#a89885',
};

const LIGHT: Omit<BrasaTheme, 'isDark' | 'mode'> = {
  accent: '#ff7a1a',
  accentContrast: '#ffffff',
  bgBase: '#ffffff',
  bgRaised: '#fffaf3',
  bgInset: '#fff3e6',
  bgInsetAlt: '#faf5ec',
  line: 'rgba(120,72,20,0.12)',
  lineSoft: 'rgba(120,72,20,0.07)',
  tx1: '#221607',
  tx2: '#5b4a3a',
  tx3: '#8a7663',
};

/* Océano nocturno: superficies azul pizarra, texto hielo, MISMO acento brasa
   (la brasa sobre navy es el contraste firma; el sistema sigue siendo uno). */
const OCEAN: Omit<BrasaTheme, 'isDark' | 'mode'> = {
  accent: '#ff7a1a',
  accentContrast: '#ffffff',
  bgBase: '#101a30',
  bgRaised: '#16223c',
  bgInset: '#0a1222',
  bgInsetAlt: '#0d1730',
  line: 'rgba(147,197,253,0.14)',
  lineSoft: 'rgba(147,197,253,0.08)',
  tx1: '#eaf2ff',
  tx2: '#c3d4ee',
  tx3: '#8ba1c1',
};

export function useBrasa(): BrasaTheme {
  const currentTheme = useSelector((state: RootState) => state.auth.theme) as ThemeMode;
  const mode: ThemeMode = currentTheme ?? 'dark';
  return useMemo<BrasaTheme>(() => {
    if (mode === 'light') return { ...LIGHT, isDark: false, mode };
    if (mode === 'ocean') return { ...OCEAN, isDark: true, mode };
    return { ...DARK, isDark: true, mode };
  }, [mode]);
}

export default useBrasa;
